import React, { useEffect, useState } from 'react';
import config from '../config';
import { Link } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';

const toReadableTotal = (total) => {
    return (total / 100).toFixed(2);
  }
  
const toReadableTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    var options = { weekday: 'short', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString("en-us", options) + " at " + date.toLocaleTimeString("en-us", { hour: '2-digit', minute: '2-digit' });
}

const ViewOrders = ({ testMode = false }) => {

  // const host = testMode ? config.testBackendUrl : config.backendUrl;
  const host = config.backendUrl;

  const [orders, setOrders] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [pagination, setPagination] = useState(0)

  const [filterStatus, setFilterStatus] = useState('');
  const [filterDate, setFilterDate] = useState('');

  const [autoPDF, setAutoPDF] = useState(false);
  const [autoSend, setAutoSend] = useState(false);

  const [authToken, setAuthToken] = useState('');

  useEffect(() => {
    console.log("Host URL:", host);

    const getAuthToken = async () => {
      try {
        const session = await fetchAuthSession();
        const token = session.tokens.idToken.toString();
        setAuthToken(token);
      } catch (error) {
        console.error('Error getting auth token:', error);
      }
    };
    
    getAuthToken();
  }, []);

  useEffect(() => {
    if (!authToken) return; // Don't fetch if no token yet
    
    fetch(`${host}/api/admin/orders/${20}/${pagination}`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
    .then(response => {
      if (!response.ok) {
        console.error('Network response was not ok', response);
        throw new Error('Network response was not ok');
      }
      return response.json();
    })
    .then(data => {
      let filteredOrders = data;

      if (filterStatus) {
          filteredOrders = filteredOrders.filter(order => order.status === filterStatus);
      }

      if (filterDate) {
          const filterDateTimestamp = new Date(filterDate).getTime() / 1000;
          filteredOrders = filteredOrders.filter(order => {
              const orderDate = new Date(order.timestamp * 1000);
              return orderDate.toDateString() === new Date(filterDateTimestamp * 1000).toDateString();
          });
      }

      const sortedOrders = filteredOrders.sort((a, b) => b.timestamp - a.timestamp);
      setOrders(sortedOrders);
    })
    .catch(error => {
      console.error('Error fetching orders:', error);
    });
  }, [refresh, filterStatus, filterDate, pagination, authToken]);

  useEffect(() => {
    if (!authToken) return; // Don't fetch if no token yet

    fetch(`${host}/api/admin/settings`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      }
    })
    .then(response => response.json())
    .then(data => {
      setAutoPDF(data["autopdf"]);
      setAutoSend(data["autosend"]);
    })
    .catch(error => {
      console.error('Error fetching settings:', error);
    });
  }, [authToken]);

  const getStatusBadge = (status) => {
    const statusMap = {
      'purchase_pending': { class: 'badge-warning', text: 'Pending' },
      'order_placed': { class: 'badge-success', text: 'Placed' },
      'cancelled': { class: 'badge-error', text: 'Cancelled' },
      'refunded': { class: 'badge-secondary', text: 'Refunded' }
    };
    
    const statusInfo = statusMap[status] || { class: 'badge-secondary', text: status };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.text}</span>;
  };

  return (
    <div className="main-content">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-header-title">
            View Orders
            {testMode && <span style={{color: 'var(--warning-color)'}}> (Test Mode)</span>}
          </h1>
        </div>
        <div className="page-header-actions">
          <Link to={testMode ? "/test" : "/"} className="btn btn-secondary">
            ← Back Home
          </Link>
          <button className="btn btn-primary" onClick={() => setRefresh(!refresh)}>
            🔄 Refresh
          </button>
          <button 
            className="btn btn-ghost" 
            onClick={() => {
              window.location.href = testMode ? '/orders' : '/test/orders';
              setRefresh(!refresh);
            }}
          >
            🔄 Toggle Mode
          </button>
        </div>
      </div>

      {/* Filters Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">Filters & Search</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 'var(--spacing-lg)', flexWrap: 'wrap', alignItems: 'center' }}>
            <div className="form-group" style={{ minWidth: '150px' }}>
              <label className="form-label">Status</label>
              <select 
                className="form-select" 
                value={filterStatus} 
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="purchase_pending">Pending</option>
                <option value="order_placed">Order Placed</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
                <option value="gift_processed">Gift Processed</option>
                <option value="gift_redeemed">Gift Redeemed</option>
              </select>
            </div>

            <div className="form-group" style={{ minWidth: '150px' }}>
              <label className="form-label">Date</label>
              <input 
                type="date" 
                className="form-input"
                value={filterDate} 
                onChange={(e) => setFilterDate(e.target.value)} 
              />
            </div>

            <div className="form-group" style={{ minWidth: '200px' }}>
              <label className="form-label">Order ID</label>
              <input 
                type="text" 
                className="form-input"
                placeholder="Search by Order ID" 
                onChange={(e) => {
                  const searchValue = e.target.value;
                  if (searchValue) {
                    setOrders(orders.filter(order => order.id.includes(searchValue)));
                  } else {
                    setRefresh(!refresh); // Reset to original orders
                  }
                }} 
              />
            </div>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="card" style={{ marginBottom: 'var(--spacing-2xl)' }}>
        <div className="card-header">
          <h3 className="card-title">System Settings</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 'var(--spacing-xl)', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <input 
                type="checkbox" 
                id="autoPDF"
                checked={autoPDF} 
                onChange={(e) => {
                  if (!window.confirm("Are you sure you want to change the auto-generate setting? This will affect how orders are processed.")) {
                    e.preventDefault();
                    return;
                  }

                  const newValue = e.target.checked;
                  setAutoPDF(newValue);
                  fetch(`${host}/api/admin/settings`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${authToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ id: 'autopdf', enabled: newValue }),
                  })
                  .then(response => response.json())
                  .then(data => {
                    console.log('Settings updated:', data);
                  })
                  .catch(error => {
                    console.error('Error updating settings:', error);
                  });
                }}
              />
              <label htmlFor="autoPDF" style={{ margin: 0, cursor: 'pointer' }}>
                Auto-generate PDFs
              </label>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
              <input 
                type="checkbox"
                id="autoSend"
                checked={autoSend} 
                onChange={(e) => {
                  if (!window.confirm("Are you sure you want to change the auto-send setting? This will affect how orders are processed.")) {
                    e.preventDefault();
                    return;
                  }

                  const newValue = e.target.checked;
                  setAutoSend(newValue);
                  fetch(`${host}/api/admin/settings`, {
                    method: 'POST',
                    headers: {
                      Authorization: `Bearer ${authToken}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ id: "autosend", enabled: newValue })
                  })
                  .then(response => response.json())
                  .then(data => {
                    console.log('Settings updated:', data);
                  })
                  .catch(error => {
                    console.error('Error updating settings:', error);
                  });
                }}
              />
              <label htmlFor="autoSend" style={{ margin: 0, cursor: 'pointer' }}>
                Auto-send orders to print
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: 'var(--spacing-lg)',
        padding: 'var(--spacing-md)',
        backgroundColor: 'var(--bg-primary)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-light)'
      }}>
        <button 
          className="btn btn-secondary" 
          onClick={() => setPagination(pagination - 1)}
          disabled={pagination === 0}
        >
          ← Previous
        </button>
        <span style={{ color: 'var(--text-secondary)', fontWeight: 'var(--font-weight-medium)' }}>
          Showing orders {pagination * 20} - {(pagination * 20) + 20}
        </span>
        <button 
          className="btn btn-secondary" 
          onClick={() => setPagination(pagination + 1)}
        >
          Next →
        </button>
      </div>

      {/* Orders Table */}
      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Order ID</th>
              <th>Amount</th>
              <th>Timestamp</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <tr key={order.id}>
                <td style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 'var(--font-weight-medium)' }}>
                  {order.id}
                </td>
                <td style={{ fontWeight: 'var(--font-weight-semibold)', color: 'var(--text-primary)' }}>
                  ${toReadableTotal(order.cost.total)}
                </td>
                <td>{toReadableTimestamp(order.timestamp)}</td>
                <td>{getStatusBadge(order.status)}</td>
                <td>
                  <Link 
                    to={order.status === "gift_processed" || order.promotion_code ? (testMode ? `/test/orders/gift/${order.id}` : `/orders/gift/${order.id}`) : (testMode ? `/test/orders/${order.id}` : `/orders/${order.id}`)}
                    className="btn btn-sm btn-primary"
                  >
                    View Details
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {orders.length === 0 && (
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center', padding: 'var(--spacing-3xl)' }}>
            <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-lg)' }}>📋</div>
            <h3 style={{ marginBottom: 'var(--spacing-md)' }}>No Orders Found</h3>
            <p style={{ color: 'var(--text-secondary)' }}>
              {filterStatus || filterDate ? 'Try adjusting your filters to see more results.' : 'Orders will appear here once they are placed.'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewOrders;